import { useId, useState } from 'react'
import { Check, Copy, KeyRound, Pencil, UserPlus, UserX, Users } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { canSeeMoney, type RoleCode } from '@cattle/money'
import type { PatchPersonRequest, ResetPasswordResponse } from '@cattle/contracts'

import {
  Avatar,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Field,
  LoadingSkeleton,
  Modal,
  PageHeader,
  QueryBoundary,
  RowAction,
  Section,
  Select,
  StatusBadge,
  TextInput,
  useToast,
  type Column,
  type StatusTone,
} from '../../../components'
import { queryKeys, usePeople, type Person } from '../../../fixtures'
import { patch, post } from '../../../lib/apiClient'
import { t, type MessageKey } from '../../../lib/i18n'
import { useApiMutation } from '../../../lib/mutations'
import { routes } from '../../../lib/routes'

/**
 * O-02 · People.
 *
 * "Sees money" is its own column because it is the question he asks most, and a
 * table answers it for every account at once without opening anything. The
 * answer is computed from the role by `canSeeMoney()` — the same predicate the
 * server gates the payload with — so the column can never drift from the rule.
 *
 * TWO RULES THE WIRING TURNS ON
 *
 * **The last owner is the server's rule, not this screen's.** The API answers
 * `409 LAST_OWNER` on a deactivate or a demote that would leave a book with no
 * owner, and the sentence it sends says what to do about it. This screen used to
 * hide those controls behind its own count of the rows it had loaded — a count
 * taken over a list that includes DEACTIVATED people, so it could disagree with
 * the server in both directions: hiding a control that was allowed, and offering
 * one that was not. The controls now render, and the server's own words are what
 * the reader sees.
 *
 * **A one-time password is shown once.** `POST /people/:id/reset-password`
 * returns it and there is no route that returns it again, so the reveal carries
 * a copy button and says plainly that it cannot be shown a second time.
 */

const ROLE_TONE: Record<RoleCode, StatusTone> = {
  OWNER: 'ok',
  PARTNER: 'warn',
  STAFF: 'neutral',
}

const ROLES: RoleCode[] = ['OWNER', 'PARTNER', 'STAFF']

/** A matrix cell is a word, never a bare tick: colour alone is not an answer. */
type Allowance = 'YES' | 'NO' | 'OPTIONAL'

interface MatrixRow {
  id: string
  label: MessageKey
  cells: Record<RoleCode, Allowance>
}

const MATRIX: MatrixRow[] = [
  {
    id: 'animals',
    label: 'o.people.matrixAnimals',
    cells: { OWNER: 'YES', PARTNER: 'YES', STAFF: 'YES' },
  },
  {
    id: 'expenses',
    label: 'o.people.matrixExpenses',
    cells: { OWNER: 'YES', PARTNER: 'YES', STAFF: 'YES' },
  },
  {
    id: 'prices',
    label: 'o.people.matrixPrices',
    cells: { OWNER: 'YES', PARTNER: 'YES', STAFF: 'NO' },
  },
  {
    id: 'sale',
    label: 'o.people.matrixSale',
    cells: { OWNER: 'YES', PARTNER: 'YES', STAFF: 'OPTIONAL' },
  },
  {
    id: 'admin',
    label: 'o.people.matrixAdmin',
    cells: { OWNER: 'YES', PARTNER: 'NO', STAFF: 'NO' },
  },
]

const HOW_STAFF: MessageKey[] = [
  'o.people.howStaff1',
  'o.people.howStaff2',
  'o.people.howStaff3',
  'o.people.howStaff4',
]

function AllowanceBadge({ value }: { value: Allowance }) {
  if (value === 'YES') return <StatusBadge tone="ok">{t('label.yes')}</StatusBadge>
  if (value === 'NO') return <StatusBadge tone="danger">{t('label.no')}</StatusBadge>
  return <StatusBadge tone="neutral">{t('o.people.matrixOptional')}</StatusBadge>
}

export function PeopleScreen() {
  const people = usePeople()
  const toast = useToast()
  const navigate = useNavigate()
  const nameId = useId()
  const roleId = useId()
  const noteId = useId()

  const [deactivating, setDeactivating] = useState<Person | null>(null)
  const [resetting, setResetting] = useState<Person | null>(null)
  const [editing, setEditing] = useState<Person | null>(null)
  const [editName, setEditName] = useState('')
  const [editRole, setEditRole] = useState<RoleCode>('STAFF')
  const [editNote, setEditNote] = useState('')
  /** Held only until the reveal is closed. Nothing can fetch it again. */
  const [revealed, setRevealed] = useState<{ name: string; password: string } | null>(null)

  const openEdit = (person: Person) => {
    setEditName(person.name)
    setEditRole(person.role)
    setEditNote(person.note ?? '')
    setEditing(person)
  }

  /*
    Only the fields that moved: `patchPersonRequest` refuses an empty body, and
    a role that is sent back unchanged is still an audit row saying it changed.
  */
  const saveEdit = useApiMutation<Person, unknown>({
    run: async (person) => {
      const body: PatchPersonRequest = {}
      if (editName.trim() !== person.name) body.name = editName.trim()
      if (editRole !== person.role) body.role = editRole
      const note = editNote.trim()
      if (note !== (person.note ?? '')) body.note = note.length > 0 ? note : null
      if (Object.keys(body).length === 0) return null
      return patch(`/people/${person.id}`, body)
    },
    // A changed role changes what the SERVER sends that account, so anything
    // rendered from a person's role has moved with it.
    invalidates: [queryKeys.people, queryKeys.ownerOverview],
    success: (_result, person) => t('o.people.saved', { name: person.name }),
    onDone: () => setEditing(null),
  })

  const deactivate = useApiMutation<Person, unknown>({
    run: async (person) => post(`/people/${person.id}/deactivate`),
    invalidates: [queryKeys.people, queryKeys.ownerOverview],
    success: (_result, person) => t('o.people.deactivated', { name: person.name }),
  })

  const resetPassword = useApiMutation<Person, ResetPasswordResponse>({
    run: async (person) => post<ResetPasswordResponse>(`/people/${person.id}/reset-password`),
    invalidates: [queryKeys.people],
    onDone: (result, person) =>
      setRevealed({ name: person.name, password: result.oneTimePassword }),
  })

  const copyPassword = (password: string) => {
    void navigator.clipboard.writeText(password).then(
      () => toast.show(t('o.people.passwordCopied')),
      () => toast.show(t('o.people.newPasswordBody'), 'danger'),
    )
  }

  const columns: Column<Person>[] = [
    {
      id: 'name',
      header: t('label.name'),
      sortBy: (person) => person.name,
      cell: (person) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={person.name} />
          <div className="min-w-0">
            <div className="font-semibold text-dark">{person.name}</div>
            <div className="text-[11.5px] text-muted">
              {person.isYou ? t('o.people.you') : person.note}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'username',
      header: t('label.username'),
      cell: (person) => <span className="tnum text-sub">{person.username}</span>,
    },
    {
      id: 'role',
      header: t('o.people.colCanDo'),
      cell: (person) => (
        <StatusBadge tone={ROLE_TONE[person.role]}>{t(`role.${person.role}`)}</StatusBadge>
      ),
    },
    {
      id: 'seesMoney',
      header: t('o.people.colSeesMoney'),
      cell: (person) =>
        canSeeMoney(person.role) ? (
          <StatusBadge tone="ok">{t('label.yes')}</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">{t('label.no')}</StatusBadge>
        ),
    },
    {
      id: 'lastSignedIn',
      header: t('label.lastSignedIn'),
      secondary: true,
      cell: (person) => <span className="text-sub">{person.lastSignedIn}</span>,
    },
  ]

  const matrixColumns: Column<MatrixRow>[] = [
    {
      id: 'what',
      header: '',
      cell: (row) => <span className="text-sub">{t(row.label)}</span>,
    },
    ...(['OWNER', 'PARTNER', 'STAFF'] as const).map<Column<MatrixRow>>((role) => ({
      id: role,
      header: t(`role.${role}`),
      numeric: true,
      cell: (row) => <AllowanceBadge value={row.cells[role]} />,
    })),
  ]

  return (
    <>
      <QueryBoundary
        query={people}
        skeleton={<LoadingSkeleton variant="table" rows={3} />}
        isEmpty={(rows) => rows.length === 0}
        empty={
          <EmptyState
            icon={<Users className="size-6" />}
            title={t('o.people.title')}
            body={t('o.newPerson.subtitle')}
            action={
              <Button
                variant="primary"
                icon={<UserPlus className="size-4" />}
                onClick={() => navigate(routes.owner.newPerson())}
              >
                {t('o.people.add')}
              </Button>
            }
          />
        }
      >
        {(rows) => (
          <>
            <PageHeader
              title={t('o.people.title')}
              subtitle={t('o.people.subtitle', { count: rows.length })}
              actions={
                <Link to={routes.owner.newPerson()}>
                  <Button variant="primary" icon={<UserPlus className="size-4" />}>
                    {t('o.people.add')}
                  </Button>
                </Link>
              }
            />

            <Section className="mb-3.5">
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(person) => person.id}
                caption={t('o.people.title')}
                rowActions={(person) => (
                  <>
                    <RowAction
                      label={t('o.people.editRole')}
                      icon={<Pencil className="size-full" />}
                      onClick={() => openEdit(person)}
                    />
                    <RowAction
                      label={t('o.people.resetPassword')}
                      icon={<KeyRound className="size-full" />}
                      onClick={() => setResetting(person)}
                    />
                    <RowAction
                      label={t('o.people.deactivate')}
                      tone="danger"
                      icon={<UserX className="size-full" />}
                      onClick={() => setDeactivating(person)}
                    />
                  </>
                )}
              />
            </Section>

            <div className="grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
              <Section title={t('o.people.matrixTitle')}>
                <DataTable
                  columns={matrixColumns}
                  rows={MATRIX}
                  rowKey={(row) => row.id}
                  caption={t('o.people.matrixTitle')}
                />
              </Section>

              <Section surface="accent" title={t('o.people.howStaffTitle')}>
                <ul className="flex flex-col gap-2.5">
                  {HOW_STAFF.map((key) => (
                    <li
                      key={key}
                      className="flex items-start gap-2 text-[12.5px] leading-snug text-sub"
                    >
                      <Check className="mt-px size-3.5 shrink-0 text-success" aria-hidden="true" />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            </div>
          </>
        )}
      </QueryBoundary>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={t('o.people.editTitle', { name: editing?.name ?? '' })}
        subtitle={t('o.people.editSubtitle')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              icon={<Check className="size-4" />}
              disabled={editName.trim().length < 2 || saveEdit.isPending}
              onClick={() => {
                if (editing) saveEdit.mutate(editing)
              }}
            >
              {t('act.saveChanges')}
            </Button>
          </>
        }
      >
        <Field required label={t('label.name')} htmlFor={nameId}>
          <TextInput id={nameId} value={editName} onChange={setEditName} className="max-w-none" />
        </Field>
        <Field label={t('o.people.colCanDo')} htmlFor={roleId}>
          <Select<RoleCode>
            id={roleId}
            value={editRole}
            onChange={setEditRole}
            options={ROLES.map((role) => ({ value: role, label: t(`role.${role}`) }))}
          />
        </Field>
        <Field label={t('o.people.noteLabel')} hint={t('o.people.noteHint')} htmlFor={noteId}>
          <TextInput id={noteId} value={editNote} onChange={setEditNote} className="max-w-none" />
        </Field>
      </Modal>

      <ConfirmDialog
        open={resetting !== null}
        onClose={() => setResetting(null)}
        onConfirm={() => {
          if (resetting) resetPassword.mutate(resetting)
        }}
        tone="danger"
        disabled={resetPassword.isPending}
        title={t('o.people.resetTitle', { name: resetting?.name ?? '' })}
        body={t('o.people.resetBody')}
        confirmLabel={t('o.people.resetConfirm')}
      />

      {/*
        The whole of the reader's opportunity. `resetPasswordResponse` is the
        only place this string ever exists in plain text — the server keeps an
        argon2 hash — so it is copied here or it is gone.
      */}
      <Modal
        open={revealed !== null}
        onClose={() => setRevealed(null)}
        title={t('o.people.newPasswordTitle', { name: revealed?.name ?? '' })}
        footer={
          <Button variant="primary" onClick={() => setRevealed(null)}>
            {t('act.close')}
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="flex h-11 items-center rounded-[10px] border border-glass-border bg-glass px-4 font-display text-lg font-bold tracking-[0.10em] tnum text-dark">
            {revealed?.password}
          </span>
          <Button
            icon={<Copy className="size-3.5" />}
            onClick={() => copyPassword(revealed?.password ?? '')}
          >
            {t('act.copy')}
          </Button>
        </div>
        <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
          {t('o.people.newPasswordBody')}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-muted">{t('o.people.mustChange')}</p>
      </Modal>

      <ConfirmDialog
        open={deactivating !== null}
        onClose={() => setDeactivating(null)}
        onConfirm={() => {
          if (deactivating) deactivate.mutate(deactivating)
        }}
        tone="danger"
        disabled={deactivate.isPending}
        title={t('o.people.deactivateTitle', { name: deactivating?.name ?? '' })}
        body={t('o.people.deactivateBody')}
        confirmLabel={t('o.people.deactivate')}
      />
    </>
  )
}
